import { CONTENTFUL_PREVIEW_PROXY_PATH } from '../services/contentfulPreviewService'

// Develop-only proxy (see gatsbyHelpers/contentfulPreviewProxy.js). Do not POST Gatsby's
// `/__refresh`: when GATSBY_REFRESH_TOKEN is set the browser must send it as Authorization,
// and without it Gatsby returns 403 so drafts stay stale. The proxy emits the same webhook
// server-side and narrows sourceNodes to gatsby-source-contentful.
export const GATSBY_REFRESH_ENDPOINT = `${CONTENTFUL_PREVIEW_PROXY_PATH}/refresh`

/** Long enough for a cold prod-sized re-source; the server's own wait is 10 minutes. */
const DEFAULT_TIMEOUT_MS = 600000
const DEFAULT_POLL_INTERVAL_MS = 2000

type RefreshListener = (isRefreshing: boolean) => void

type RefreshJob = {
  id: string
  status: 'pending' | 'done' | 'error'
  durationMs: number | null
  error: string | null
}

type RefreshOptions = {
  fetchImpl?: typeof fetch
  pollIntervalMs?: number
  timeoutMs?: number
  sleep?: (ms: number) => Promise<void>
}

/**
 * Survives the page reload that webpack forces part-way through a re-source.
 *
 * Rebuilding the dev bundle changes its hash, so HMR gives up with "Cannot find update (Full
 * reload needed)" and reloads the iframe while the re-source is still running. That wipes
 * `inFlightCount` and kills the poll loop, so the banner vanished mid-rebuild and the reload that
 * shows the fresh data never happened. The job itself is server-side and unaffected — only the
 * client's handle to it was lost, so the id is all that needs to survive.
 */
const REFRESH_JOB_STORAGE_KEY = 'pf-preview-refresh-job'

const readStoredJobId = (): string | undefined => {
  try {
    return sessionStorage.getItem(REFRESH_JOB_STORAGE_KEY) || undefined
  } catch {
    return undefined
  }
}

const storeJobId = (id: string | undefined): void => {
  try {
    if (id) sessionStorage.setItem(REFRESH_JOB_STORAGE_KEY, id)
    else sessionStorage.removeItem(REFRESH_JOB_STORAGE_KEY)
  } catch {
    // Private mode / blocked storage: the refresh still works, it just cannot survive a reload.
  }
}

const listeners = new Set<RefreshListener>()
let inFlightCount = 0

const notifyRefreshListeners = (): void => {
  const isRefreshing = inFlightCount > 0
  listeners.forEach(listener => listener(isRefreshing))
}

const defaultSleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Subscribe to whether a Contentful → Gatsby refresh is in flight.
 * Used by the preview iframe banner so editors can see when re-source was triggered.
 */
export const subscribeGatsbyContentfulRefresh = (listener: RefreshListener): (() => void) => {
  listeners.add(listener)
  listener(inFlightCount > 0)
  return () => {
    listeners.delete(listener)
  }
}

export const isGatsbyContentfulRefreshInFlight = (): boolean => inFlightCount > 0

const startRefresh = async (fetchImpl: typeof fetch): Promise<RefreshJob> => {
  // Split RUM wraps window.fetch and App Service CORS is `Access-Control-Allow-Origin: *`.
  // `credentials: 'omit'` keeps this a non-credentialed request so the preflight cannot fail
  // closed as TypeError: Failed to fetch.
  const response = await fetchImpl(GATSBY_REFRESH_ENDPOINT, { method: 'POST', credentials: 'omit' })

  if (!response.ok) {
    throw new Error(`Refresh could not be started, status ${response.status}`)
  }

  const job: RefreshJob = await response.json()

  if (!job || typeof job.id !== 'string') {
    throw new Error('Refresh response did not include a job id')
  }

  return job
}

/** Thrown when the server no longer knows the job — it restarted, or the job aged out. */
class UnknownRefreshJobError extends Error {}

const readRefreshJob = async (id: string, fetchImpl: typeof fetch): Promise<RefreshJob> => {
  const response = await fetchImpl(`${GATSBY_REFRESH_ENDPOINT}/${id}`, {
    credentials: 'omit',
    cache: 'no-store',
  })

  // 404 is not a dropped request: the job is gone and polling can never succeed. Resuming after a
  // develop restart would otherwise spin for the full timeout showing a banner for nothing.
  if (response.status === 404) {
    throw new UnknownRefreshJobError(`Refresh job ${id} is no longer known to the server`)
  }

  if (!response.ok) {
    throw new Error(`Refresh status for job ${id} failed with status ${response.status}`)
  }

  return response.json()
}

/**
 * Polls one already-started job to completion, keeping listeners informed for its whole life.
 * Shared by starting a refresh and by resuming one across a reload.
 */
const pollJobToCompletion = async (
  id: string,
  {
    fetchImpl = fetch,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    sleep = defaultSleep,
  }: RefreshOptions,
): Promise<void> => {
  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    await sleep(pollIntervalMs)

    // A dropped poll is not a failed refresh — the server is single-threaded and busy
    // re-sourcing, so a request can fail while the job is perfectly healthy. Keep polling
    // until the deadline and let the deadline be the only thing that gives up.
    let current: RefreshJob
    try {
      current = await readRefreshJob(id, fetchImpl)
    } catch (error) {
      if (error instanceof UnknownRefreshJobError) throw error
      continue
    }

    if (current.status === 'done') return
    if (current.status === 'error') throw new Error(current.error || 'Refresh failed')
  }

  throw new Error(`Refresh did not finish within ${timeoutMs}ms`)
}

/**
 * Asks the develop server to re-source `gatsby-source-contentful` so page queries pick up
 * Preview API changes the live overlay cannot map (new pages, slug changes, added/removed sections).
 *
 * Starts the job, then **polls** for it. The request must not be held open for the duration:
 * `dev.bwll.net` sits behind Cloudflare, whose origin timeout is 100s, and a re-source takes
 * longer than that. The previous version waited for the server to finish before responding, so
 * Cloudflare answered 524 while the re-source went on to succeed — this rejected, the caller's
 * `.then()` never ran, and the fresh data was never applied. Each poll is a short request, so
 * there is nothing left for the proxy to time out.
 *
 * The job id is persisted before polling so `resumeGatsbyContentfulRefresh` can pick it up if
 * webpack reloads the iframe mid-rebuild.
 */
export const refreshGatsbyContentful = async (options: RefreshOptions = {}): Promise<void> => {
  const fetchImpl = options.fetchImpl || fetch

  inFlightCount += 1
  notifyRefreshListeners()

  try {
    const job = await startRefresh(fetchImpl)
    storeJobId(job.id)

    if (job.status === 'done') return
    if (job.status === 'error') throw new Error(job.error || 'Refresh failed')

    await pollJobToCompletion(job.id, options)
  } catch (error) {
    console.error(`Live preview could not complete ${GATSBY_REFRESH_ENDPOINT}, drafts may be stale.`, error)
    throw error
  } finally {
    storeJobId(undefined)
    inFlightCount = Math.max(0, inFlightCount - 1)
    notifyRefreshListeners()
  }
}

/**
 * Reattaches to a refresh that was already running when this document loaded.
 *
 * Rebuilding the dev bundle changes its hash, so HMR gives up ("Cannot find update (Full reload
 * needed)") and reloads the iframe part-way through a re-source. Without this the banner
 * disappeared mid-rebuild and the reload that shows the fresh data never happened — the refresh
 * looked like it had silently failed, even though the server went on to finish it.
 *
 * Resolves when the resumed job finishes, so the caller can reload exactly as it would have.
 * Returns `false` when there was nothing to resume, which is the common case.
 */
export const resumeGatsbyContentfulRefresh = async (options: RefreshOptions = {}): Promise<boolean> => {
  const id = readStoredJobId()
  if (!id) return false

  // A second document polling the same job would double-count `inFlightCount` and leave the banner
  // stuck on. Only ever resume when nothing is already in flight here.
  if (inFlightCount > 0) return false

  inFlightCount += 1
  notifyRefreshListeners()

  try {
    await pollJobToCompletion(id, options)
    return true
  } catch (error) {
    console.error(`Live preview could not finish resuming refresh job ${id}.`, error)
    throw error
  } finally {
    storeJobId(undefined)
    inFlightCount = Math.max(0, inFlightCount - 1)
    notifyRefreshListeners()
  }
}
